import axios from 'axios';

export interface RegionalData {
  id: string;
  codigoRegional: string;
  nome: string;
  nomeFantasia: string;
  cnpj: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  estado: string;
  cep: string;
  email: string;
  website: string;
  telefone: string;
  situacao: string;
  createdAt: string;
  updatedAt: string;
  base_tag: string;
}

export interface ConsultorData {
  id: string;
  codigo: string;
  nome: string;
  email?: string;
  telefone?: string;
  situacao: string;
  createdAt: string;
  updatedAt: string;
}

// Cooperativa e Voluntário (equipes) conforme novas rotas
export interface CooperativaData {
  id: string;
  codigoCooperativa: string;
  nome: string;
  situacao?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface VoluntarioData {
  id: string;
  codigoVoluntario: string;
  nome: string;
  situacao?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface PagedResponse<T> {
  totalItens: number;
  totalPages: number;
  page: number;
  limit: number;
  data: T[];
}

export interface RegionalQuery {
  page?: number;
  limit?: number;
  baseTag?: string;
  situacao?: string;
  nome?: string;
  codigoRegional?: string;
  periodo?: string; // mm-yyyy
}

export interface CooperativaQuery {
  page?: number;
  limit?: number;
  baseTag?: string;
  situacao?: string;
  nome?: string;
  codigoCooperativa?: string;
  periodo?: string; // mm-yyyy
}

export interface VoluntarioQuery {
  page?: number;
  limit?: number;
  baseTag?: string;
  situacao?: string;
  nome?: string;
  codigoVoluntario?: string;
  periodo?: string; // mm-yyyy
}

// Resumo do usuário (últimos períodos)
export interface UsuarioResumoItem {
  periodo: string; // YYYY-MM
  total_contratos: number;
  total_gerado: number;
  novos_contratos: number;
  contratos_perdidos: number;
}

export interface UsuarioResumoResponse {
  limit: number;
  data: UsuarioResumoItem[];
}

// Resumo período para dashboard
export interface ResumoPeriodoBloco {
  quantidade: number;
  total_gerado: string;
  total_contratos: number;
  novos_contratos: number;
  contratos_perdidos: number;
  valor_vitalicio: string;
  valor_bonificacoes: string;
  valor_descontos: string;
  valor_repasse: string;
  pagamento_voluntarios: string;
}

export interface ResumoPeriodoResponse {
  periodo: string; // YYYY-MM
  regional: ResumoPeriodoBloco;
  cooperativa: ResumoPeriodoBloco;
  voluntario: ResumoPeriodoBloco;
}

export const listRegional = async (query: RegionalQuery = {}): Promise<PagedResponse<RegionalData>> => {
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/comissionamentos/regional`;

    const response = await axios.get(url, {
      withCredentials: true,
      params: query,
    });

    return response.data as PagedResponse<RegionalData>;
  } catch (error: unknown) {
    console.error('❌ Erro ao listar regionais:', error);

    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao listar regionais');
    } else {
      throw new Error('Erro desconhecido');
    }
  }
};

// GET /comissionamentos/resumo-periodo?periodo=mm-yyyy
export const getResumoPeriodo = async (periodo: string): Promise<ResumoPeriodoResponse> => {
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/comissionamentos/resumo-periodo`;
    const response = await axios.get(url, {
      withCredentials: true,
      params: { periodo },
    });
    return response.data as ResumoPeriodoResponse;
  } catch (error: unknown) {
    console.error('❌ Erro ao obter resumo do período:', error);
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao obter resumo do período');
    } else {
      throw new Error('Erro desconhecido');
    }
  }
};

export const getRegional = async (codigoRegional: string) => {
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/comissionamentos/regional/${codigoRegional}`;

    const response = await axios.get(url, {
      withCredentials: true,
    });

    return response.data;
  } catch (error: unknown) {
    console.error('❌ Erro ao buscar regional:', error);

    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar regional');
    } else {
      throw new Error('Erro desconhecido');
    }
  }
};

export const fetchRegionais = async () => {
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/comissionamentos/indexRegional`;

    const response = await axios.get(url, {
      withCredentials: true,
    });

    return response.data;
  } catch (error: unknown) {
    console.error('❌ Erro ao buscar regionais:', error);

    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar regionais');
    } else {
      throw new Error('Erro desconhecido');
    }
  }
};

export const fetchConsultores = async () => {
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/comissionamentos/indexConsultor`;

    const response = await axios.get(url, {
      withCredentials: true,
    });

    return response.data;
  } catch (error: unknown) {
    console.error('❌ Erro ao buscar consultores:', error);

    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar consultores');
    } else {
      throw new Error('Erro desconhecido');
    }
  }
};

// Novas rotas com filtros e paginação
export const listConsultor = async (query: CooperativaQuery = {}): Promise<PagedResponse<CooperativaData>> => {
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/comissionamentos/consultor`;
    const response = await axios.get(url, {
      withCredentials: true,
      params: query,
    });
    return response.data as PagedResponse<CooperativaData>;
  } catch (error: unknown) {
    console.error('❌ Erro ao listar cooperativas:', error);
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao listar cooperativas');
    } else {
      throw new Error('Erro desconhecido');
    }
  }
};

export const listEquipe = async (query: VoluntarioQuery = {}): Promise<PagedResponse<VoluntarioData>> => {
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/comissionamentos/equipe`;
    const response = await axios.get(url, {
      withCredentials: true,
      params: query,
    });
    return response.data as PagedResponse<VoluntarioData>;
  } catch (error: unknown) {
    console.error('❌ Erro ao listar voluntários:', error);
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao listar voluntários');
    } else {
      throw new Error('Erro desconhecido');
    }
  }
};

// GET /comissionamentos/usuario/resumo
export const getUsuarioResumo = async (params: {
  qualificado_tipo?: 'REGIONAL' | 'COOPERATIVA' | 'VOLUNTARIO';
  qualificado_uuid?: string;
  base_tag?: string;
} = {}): Promise<UsuarioResumoResponse> => {
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/comissionamentos/usuario/resumo`;
    const response = await axios.get(url, {
      withCredentials: true,
      params: params,
    });
    return response.data as UsuarioResumoResponse;
  } catch (error: unknown) {
    console.error('❌ Erro ao obter resumo do usuário:', error);
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao obter resumo do usuário');
    } else {
      throw new Error('Erro desconhecido');
    }
  }
};

/** Desfecho da última varredura de boletos do SGA por data de vencimento. */
export interface StatusSyncBoletos {
  /**
   * ISO da última execução que **trouxe boleto do SGA**. `null` = nunca houve uma (não é erro).
   *
   * ⚠️ Não é "a última execução": uma que falhou na autenticação e gravou zero boletos NÃO data
   * o carimbo — foi o defeito encontrado no teste de 03/09/2026, em que a tela dizia "Dados
   * atualizados em 21:33" sobre uma varredura que não atualizou nada.
   */
  ultimaAtualizacao: string | null;
  ultimoStatus: string | null;
  /** A última execução concluída, tenha ela trazido dado ou não. */
  ultimaTentativa: { em: string; status: string; trouxeDados: boolean } | null;
  emAndamento: boolean;
  disponivel: true;
}

/**
 * Alimenta o carimbo "Dados atualizados em" das telas do módulo (comissionamentos/at-01).
 *
 * ⚠️ **Sem catch de propósito** (regra da at-60): "nunca sincronizado" e "não consegui perguntar"
 * levam a leituras opostas — a primeira é um estado legítimo do sistema, a segunda é uma falha
 * que o usuário precisa ver. Engolir aqui faria a tela carimbar "nunca atualizado" sobre um
 * backend fora do ar. Quem trata o erro é o componente, que distingue os dois.
 */
export const getStatusSyncBoletos = async (): Promise<StatusSyncBoletos> => {
  const url = `${process.env.REACT_APP_BACKEND_URL}/comissionamentos/sync-boletos/status`;
  const response = await axios.get(url, { withCredentials: true });
  return response.data as StatusSyncBoletos;
};